import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IPurchaseOrder, IPurchaseOrderInput, purchaseOrdersAtom, purchaseOrdersLoadingAtom } from "./types";
import { GQLPaginatedResponseType } from "../../globals/types";

type UsePurchaseOrders = [IPurchaseOrder[], {
    list: (query?: Record<string, any>) => Promise<IPurchaseOrder[] | null>;
    getById: (id: number) => Promise<IPurchaseOrder | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<GQLPaginatedResponseType<IPurchaseOrder> | null>;
    create: (input: IPurchaseOrderInput) => Promise<IPurchaseOrder | null>;
    update: (id: number, input: IPurchaseOrderInput) => Promise<IPurchaseOrder | null>;
    remove: (id: number) => Promise<IPurchaseOrder | null>;
    confirm: (id: number) => Promise<IPurchaseOrder | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const usePurchaseOrders = (): UsePurchaseOrders => {
    const [items, setItems] = useAtom(purchaseOrdersAtom);
    const setLoading = useSetAtom(purchaseOrdersLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getPurchaseOrders(query: $query){ id po_number vendor_po_number tracking_link vendor_id status expected_date notes created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getPurchaseOrders);
            return data.getPurchaseOrders as IPurchaseOrder[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch purchase orders.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getPurchaseOrderById(id:$id){ id po_number vendor_po_number tracking_link vendor_id status expected_date notes created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getPurchaseOrderById as IPurchaseOrder;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedPurchaseOrders(first:$first,offset:$offset,query:$query){ data{ id po_number vendor_po_number tracking_link vendor_id status expected_date notes created_at updated_at } total } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            const res = data.getPaginatedPurchaseOrders as GQLPaginatedResponseType<IPurchaseOrder>;
            setItems(res.data);
            return res;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IPurchaseOrderInput) => {
        try { const q = `mutation($input:PurchaseOrderInput!){ createPurchaseOrder(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createPurchaseOrder as IPurchaseOrder;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IPurchaseOrderInput) => {
        try { const q = `mutation($id:ID!,$input:PurchaseOrderInput!){ updatePurchaseOrder(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updatePurchaseOrder as IPurchaseOrder;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deletePurchaseOrder(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deletePurchaseOrder as IPurchaseOrder;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const confirm = async (id: number) => {
        try { const q = `mutation($id:ID!){ confirmPO(id:$id){ id status } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.confirmPO as IPurchaseOrder;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove, confirm }];
}


